import React, { useContext, useEffect, useRef, useState } from 'react';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { sendErrorToSentry } from 'ErrorBoundary/sendErrorToSentry';

const TURNSTILE_SCRIPT_NOT_LOADED = "Turnstile script not loaded";
const VALIDATION_FAILED_USING_TURNSTILE = "Validation failed using turnstile";
const ERR_CODE_TOKEN_EXPIRED = "cf_token_expired"

const ShowSpinner = (flag: boolean) => {
    const spinner = document.getElementById('spinner');
    if (spinner) {
        spinner.style.display = flag ? 'block' : 'none';
    }
}

interface ISuiTurnstileState {
    token: string;
    refreshToken: () => void;
    isTurnstileLoading: boolean;
};

export enum TurnstileEnabledFeature {
    klass_code_login = "klass_code_login",
    parent_and_teacher_signup = "parent_and_teacher_signup"
}

const SuiTurnstileContext = React.createContext({} as ISuiTurnstileState);

export const SuiTurnstileProvider: React.FC<{ userType: string, action: string, featureName: TurnstileEnabledFeature, dontShowSpinner: boolean }> = ({ userType, action, children, featureName, dontShowSpinner }) => {
    const ref = React.useRef<TurnstileInstance>();
    const attemptRef = useRef<number>(0);
    const [token, setToken] = useState<string>();
    const securityFlowEnabled = window?.turnstile_enabled_features?.[featureName];
    const [isTurnstileLoading, setTurnstileLoading] = useState(false);

    useEffect(() => {
        if (securityFlowEnabled) {
            setTurnstileLoading(true);
        }
        return () => {
            setTurnstileLoading(false);
            ShowSpinner(false);
            window.cfTurnstileToken = null;
        }
    }, []);

    useEffect(() => {
        if(!dontShowSpinner) {
            ShowSpinner(isTurnstileLoading);
        }
    }, [isTurnstileLoading])

    const logEvent = (msg: string, errorCode: string) => {
        const eventData = {
            validatedUserType: userType,
            custom_user_type: "unverified_user",
            error_code: errorCode
        };
        const trackingOptions = { send_datadump: true, send_mixpanel: true, send_clevertap: false };
        trackEventsForGuests(msg, eventData, trackingOptions);
        sendErrorToSentry(new Error(msg), {errorCode});
        setTimeout(() => ShowSpinner(false), 2000);
    }

    const clearToken = () => {
        setToken(null);
        window.cfTurnstileToken = null;
    }

    const refreshToken = () => {
        if (securityFlowEnabled) {
            if (attemptRef.current < 2) {
                setTurnstileLoading(true);
                ref?.current?.reset();
            }
            attemptRef.current++;
        }
    }

    const refreshOnTokenExpiry = () => {
        clearToken();
        if (ref.current?.isExpired()) {
            refreshToken();
        }
    }

    const handleTurnstileError = (errorCode: string) => {
        setTurnstileLoading(false);
        refreshOnTokenExpiry();
        logEvent(VALIDATION_FAILED_USING_TURNSTILE, errorCode)
    }

    const handleOnExpire = (token: string) => {
        setTurnstileLoading(false);
        refreshOnTokenExpiry();
        logEvent(VALIDATION_FAILED_USING_TURNSTILE, ERR_CODE_TOKEN_EXPIRED);
    }

    const handleTurnstileSuccess = (token: string) => {
        setToken(token);
        window.cfTurnstileToken = token;
        setTurnstileLoading(false);
    }

    return (
        <SuiTurnstileContext.Provider value={{ token, refreshToken, isTurnstileLoading }}>
            {children}
            {
                (securityFlowEnabled && APP_CONFIG?.turnstile?.siteKey) &&
                <Turnstile
                    ref={ref}
                    siteKey={APP_CONFIG?.turnstile?.siteKey}
                    onError={handleTurnstileError}
                    onExpire={handleOnExpire}
                    onSuccess={handleTurnstileSuccess}
                    scriptOptions={{
                        defer: true,
                        onError: () => {
                            clearToken();
                            setTurnstileLoading(false);
                            logEvent(TURNSTILE_SCRIPT_NOT_LOADED, "script_load_failed")
                        }
                    }}
                    options={{ action, theme: 'light' }}
                />
            }
        </SuiTurnstileContext.Provider>
    )
}

export const useSuiTurnstileState = () => {
    const context = useContext(SuiTurnstileContext);
    if (!context) {
        console.warn(`Use useSuiTurnstileState in SuiTurnstileProvider`);
    }
    return context;
};